import type { Channel, EventRoute, RecipientGroup } from '@speles7172/message-client/core';
import { notificationScope } from '@peles/shared';
import { apiClient } from './client';

/**
 * Distribution groups and notification rules, through `/notification-routing`.
 *
 * `eventId` null is the office-wide scope; an id is that event's own groups and
 * rules. The server checks who may read and change each.
 */
const BASE = '/notification-routing';

export type { Channel, EventRoute as NotificationRule, RecipientGroup as NotificationGroup };

/** Somebody who may be put in a group: the office, and in an event's scope its clients. */
export interface RoutingPerson {
  id: string;
  name: string;
  /** Their address ("Client · address" from the server is split into `isClient`). */
  detail: string;
  isClient: boolean;
}

/** The server's own words for a refusal, rather than "Request failed with status code 400". */
async function said<T>(work: Promise<T>): Promise<T> {
  try {
    return await work;
  } catch (err) {
    const message = (err as { response?: { data?: { error?: { message?: unknown } } } }).response
      ?.data?.error?.message;
    throw typeof message === 'string' ? new Error(message) : err;
  }
}

function scopeParams(eventId: string | null): { scope?: string } {
  const scope = notificationScope(eventId);
  return scope ? { scope } : {};
}

export async function listNotificationGroups(eventId: string | null): Promise<RecipientGroup[]> {
  const { data } = await said(
    apiClient.get<RecipientGroup[]>(`${BASE}/groups`, { params: scopeParams(eventId) }),
  );
  return data;
}

export async function listNotificationRules(eventId: string | null): Promise<EventRoute[]> {
  const { data } = await said(
    apiClient.get<EventRoute[]>(`${BASE}/routes`, { params: scopeParams(eventId) }),
  );
  return data;
}

export interface SaveGroupInput {
  id?: string;
  eventId: string | null;
  name: string;
  description?: string | null;
  memberIds: string[];
}

export async function saveNotificationGroup(input: SaveGroupInput): Promise<RecipientGroup> {
  const body = {
    name: input.name,
    description: input.description ?? null,
    memberIds: input.memberIds,
    scope: notificationScope(input.eventId),
  };
  const { data } = await said(
    input.id
      ? apiClient.put<RecipientGroup>(`${BASE}/groups/${encodeURIComponent(input.id)}`, body)
      : apiClient.post<RecipientGroup>(`${BASE}/groups`, body),
  );
  return data;
}

/** Every rule naming a group, in any scope — `eventId` null for an office-wide rule. */
export async function notificationGroupUsage(
  id: string,
): Promise<{ event: string; eventId: string | null }[]> {
  const { data } = await said(
    apiClient.get<{ event: string; eventId: string | null }[]>(
      `${BASE}/groups/${encodeURIComponent(id)}/usage`,
    ),
  );
  return data;
}

export async function deleteNotificationGroup(id: string): Promise<void> {
  await said(apiClient.delete(`${BASE}/groups/${encodeURIComponent(id)}`));
}

export async function setNotificationRule(input: {
  eventId: string | null;
  notification: string;
  groupId: string;
  channels: Channel[];
}): Promise<EventRoute> {
  const { data } = await said(
    apiClient.put<EventRoute>(`${BASE}/routes`, {
      event: input.notification,
      groupId: input.groupId,
      channels: input.channels,
      scope: notificationScope(input.eventId),
    }),
  );
  return data;
}

export async function clearNotificationRule(input: {
  eventId: string | null;
  notification: string;
}): Promise<void> {
  await said(
    apiClient.delete(`${BASE}/routes`, {
      params: { event: input.notification, ...scopeParams(input.eventId) },
    }),
  );
}

interface PeopleOption {
  value: string;
  label: string;
  description?: string;
}

const CLIENT_PREFIX = 'Client · ';

function toPerson(option: PeopleOption): RoutingPerson {
  const description = option.description ?? '';
  const isClient = description.startsWith(CLIENT_PREFIX);
  return {
    id: option.value,
    name: option.label,
    detail: isClient ? description.slice(CLIENT_PREFIX.length) : description,
    isClient,
  };
}

/** The most pages read for the member list — 1,000 people, far past any office. */
const MAX_PEOPLE_PAGES = 20;
const PEOPLE_PAGE = 50;

/**
 * Everybody the scope's groups may hold, read whole: the office and an event's
 * clients are a short list, and a picker over all of it searches as you type.
 */
export async function listRoutingPeople(eventId: string | null): Promise<RoutingPerson[]> {
  const people: RoutingPerson[] = [];
  for (let page = 0; page < MAX_PEOPLE_PAGES; page += 1) {
    const { data } = await said(
      apiClient.get<{ options: PeopleOption[]; hasMore: boolean }>(`${BASE}/people`, {
        params: { ...scopeParams(eventId), limit: PEOPLE_PAGE, offset: page * PEOPLE_PAGE },
      }),
    );
    people.push(...data.options.map(toPerson));
    if (!data.hasMore) break;
  }
  return people;
}

/** Names for members the list above no longer offers (somebody demoted or taken off the event). */
export async function resolveRoutingPeople(
  eventId: string | null,
  ids: string[],
): Promise<RoutingPerson[]> {
  if (ids.length === 0) return [];
  const { data } = await said(
    apiClient.get<{ options: PeopleOption[] }>(`${BASE}/people/resolve`, {
      params: { ...scopeParams(eventId), values: ids.join(',') },
    }),
  );
  return data.options.map(toPerson);
}
